import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import client from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { MARRAKECH, TYPE_COLORS } from "../lib/catalog";
import { MapPin, Crosshair, Loader2, ExternalLink, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const pinIcon = (color = "#e63946") => L.divIcon({ className: "", iconSize: [26, 34], iconAnchor: [13, 34],
  html: `<svg width="26" height="34" viewBox="0 0 26 34"><path d="M13 0C5.8 0 0 5.8 0 13c0 9.5 13 21 13 21s13-11.5 13-21C26 5.8 20.2 0 13 0z" fill="${color}"/><circle cx="13" cy="13" r="5" fill="#0b0f13"/></svg>` });

export const gmapsDirections = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

// Pick a location: paste a Google Maps link, tap to drop/drag a pin, or use GPS.
export const MapPicker = ({ value, onChange, token, t: tGiven }) => {
  const ref = useRef(null); const mapRef = useRef(null); const markerRef = useRef(null);
  const [link, setLink] = useState(value?.mapsLink || "");
  const [busy, setBusy] = useState(false);
  const { t: tCtx } = useLang();
  const t = tGiven || tCtx;

  const place = (lat, lng, extra = {}) => {
    onChange({ ...value, latitude: lat, longitude: lng, ...extra });
    if (!mapRef.current) return;
    if (markerRef.current) markerRef.current.setLatLng([lat, lng]); else {
      markerRef.current = L.marker([lat, lng], { draggable: true, icon: pinIcon() }).addTo(mapRef.current);
      markerRef.current.on("dragend", (e) => { const p = e.target.getLatLng(); onChange((v) => ({ ...v, latitude: p.lat, longitude: p.lng })); });
    }
    mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 15));
  };

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const m = L.map(ref.current, { zoomControl: true }).setView(value?.latitude ? [value.latitude, value.longitude] : MARRAKECH, value?.latitude ? 15 : 13);
    L.tileLayer(TILES, { attribution: ATTR, maxZoom: 19 }).addTo(m);
    m.on("click", (e) => place(e.latlng.lat, e.latlng.lng));
    mapRef.current = m;
    if (value?.latitude) place(value.latitude, value.longitude);
    return () => { m.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolve = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try {
      const r = await client.post("/geo/resolve", { link }, token ? { headers: { "X-Submit-Token": token } } : undefined);
      place(r.data.lat, r.data.lng, { mapsLink: link });
      toast.success("Pin placed from the link");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read that link"); } finally { setBusy(false); }
  };
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device"); return; }
    navigator.geolocation.getCurrentPosition((pos) => place(pos.coords.latitude, pos.coords.longitude), () => toast.error("Couldn't get your location"), { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <div className="space-y-2" data-testid="map-picker">
      <div className="flex gap-2">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder={t("Paste a Google Maps link")} data-testid="maps-link-input" className="h-10 bg-input/60 border-white/10" />
        <Button type="button" onClick={resolve} disabled={busy} data-testid="maps-link-resolve" className="h-10 bg-white/10 text-white hover:bg-white/20">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}</Button>
        <Button type="button" onClick={locate} data-testid="maps-use-location" title={t("Use my location")} className="h-10 bg-white/10 text-white hover:bg-white/20"><Crosshair className="h-4 w-4" /></Button>
      </div>
      <div ref={ref} className="h-56 w-full overflow-hidden rounded-xl border border-white/10" data-testid="map-picker-canvas" />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="map-coords">{value?.latitude ? `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}` : t("Tap the map to drop the pin, then drag to adjust")}</span>
        <label className="flex items-center gap-2 text-white"><input type="checkbox" data-testid="location-approximate" checked={!!value?.locationApproximate} onChange={(e) => onChange({ ...value, locationApproximate: e.target.checked })} className="accent-[#e63946]" /> {t("Approximate location (Medina)")}</label>
      </div>
    </div>
  );
};

// Read-only map: one property (pin or ~150 m circle) or many (coloured by type).
export const PropertyMap = ({ properties = [], single, onSelect, height = "h-72" }) => {
  const ref = useRef(null); const mapRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const items = single ? [single] : properties.filter((p) => p.latitude && p.longitude);
    const m = L.map(ref.current, { scrollWheelZoom: !single }).setView(MARRAKECH, 13);
    L.tileLayer(TILES, { attribution: ATTR, maxZoom: 19 }).addTo(m);
    const bounds = [];
    items.forEach((p) => {
      const ll = [p.latitude, p.longitude]; bounds.push(ll);
      const color = TYPE_COLORS[p.propertyType] || "#e63946";
      const layer = p.locationApproximate && single ? L.circle(ll, { radius: 150, color, fillColor: color, fillOpacity: 0.25, weight: 2 }) : L.marker(ll, { icon: pinIcon(color) });
      layer.addTo(m);
      if (!single) layer.bindTooltip(`${p.title}`, { direction: "top", offset: [0, -30] }).on("click", () => onSelect?.(p));
    });
    if (bounds.length === 1) m.setView(bounds[0], single ? 15 : 14); else if (bounds.length > 1) m.fitBounds(bounds, { padding: [30, 30] });
    mapRef.current = m;
    return () => { m.remove(); mapRef.current = null; };
  }, [properties, single, onSelect]);
  return <div ref={ref} className={`${height} w-full overflow-hidden rounded-2xl border border-white/10`} data-testid={single ? "property-map" : "properties-map"} />;
};

export const LocationCard = ({ property: p, onAdd }) => {
  const { t } = useLang();
  if (!p.latitude || !p.longitude) {
    return (
      <div data-testid="no-location" className="glass flex flex-col items-center justify-center gap-3 rounded-2xl p-8 text-center">
        <MapPin className="h-6 w-6 text-muted-foreground" /><p className="text-sm text-muted-foreground">{t("No location added")}</p>
        {onAdd && <Button onClick={onAdd} data-testid="add-location-btn" variant="outline" className="h-9 border-white/12 bg-white/[0.03] text-white">{t("Add location")}</Button>}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="location-card">
      <PropertyMap single={p} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{p.locationApproximate ? t("Approximate") : `${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}`}</span>
        <div className="flex gap-2">
          <a href={gmapsDirections(p.latitude, p.longitude)} target="_blank" rel="noreferrer" data-testid="open-in-gmaps" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-green/15 px-3 text-sm font-medium text-brand-green hover:bg-brand-green/25"><Navigation className="h-4 w-4" /> {t("Open in Google Maps")}</a>
          {onAdd && <Button onClick={onAdd} variant="outline" data-testid="edit-location-btn" className="h-9 border-white/12 bg-white/[0.03] text-white"><ExternalLink className="h-4 w-4" /> {t("Edit")}</Button>}
        </div>
      </div>
    </div>
  );
};
